/**
 * @file shortcutKeys.ts
 * @description OS-aware formatting for keyboard-shortcut *hints*.
 *
 * The key handlers themselves accept the command modifier cross-platform via
 * `e.metaKey || e.ctrlKey`, so a binding shown as ⌘C also fires on Ctrl+C.
 * This module only changes how a binding is *displayed*. Following Apple's HIG,
 * macOS renders every modifier as a glyph (⌘ Command, ⌥ Option, ⌃ Control,
 * ⇧ Shift); every other platform spells them out (Ctrl, Alt, Shift). The
 * platform command modifier (`Cmd`) maps to ⌘ on macOS and Ctrl elsewhere.
 *
 * A literal `Ctrl` binding (e.g. the Ctrl+G grid-dot toggle, which checks
 * `ctrlKey && !metaKey` on every OS) keeps firing on Control everywhere — it
 * just displays as ⌃ on macOS and "Ctrl" elsewhere.
 */

import { useSyncExternalStore } from 'react'

/**
 * Detect macOS at runtime. SSR-safe: returns the canonical (mac) default when
 * `navigator` is unavailable so server output matches the first client render.
 */
export function detectIsMac(): boolean {
  if (typeof navigator === 'undefined') return true
  // Prefer the modern userAgentData.platform; navigator.platform is deprecated
  // and suppressed in some privacy contexts. Fall back for older Chromium.
  const uaDataPlatform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
  const platform = uaDataPlatform ?? navigator.platform ?? ''
  return /mac/i.test(platform)
}

// `useIsMac` never re-subscribes — the platform can't change within a session,
// so the store has no updates to push.
const noopSubscribe = () => () => {}

/**
 * React hook returning whether the current platform is macOS.
 *
 * Uses the canonical mac default as the server snapshot so the server-rendered
 * and hydrating client render agree (no hydration mismatch), then resolves to
 * the real platform on the client.
 */
export function useIsMac(): boolean {
  return useSyncExternalStore(noopSubscribe, detectIsMac, () => true)
}

/**
 * Map a single key token to its OS-appropriate display form. Modifier tokens
 * become macOS glyphs (⌘ ⌥ ⌃ ⇧) on macOS and spelled-out names elsewhere; the
 * platform command modifier (`Cmd`) collapses to Ctrl off macOS. Every other
 * token (letters, arrows, etc.) is returned unchanged.
 */
export function formatKeyToken(token: string, isMac: boolean): string {
  switch (token) {
    case 'Cmd':
    case 'Command':
    case 'Meta':
    case '⌘':
      return isMac ? '⌘' : 'Ctrl'
    case 'Alt':
    case 'Option':
    case '⌥':
      return isMac ? '⌥' : 'Alt'
    case 'Ctrl':
    case 'Control':
    case '⌃':
      return isMac ? '⌃' : 'Ctrl'
    case 'Shift':
    case '⇧':
      return isMac ? '⇧' : 'Shift'
    default:
      return token
  }
}

/**
 * Format a whole combo string for the OS, preserving its original separators
 * (spaces, `+`, `/`). Tokens are the runs between separators, so this handles
 * hints like `⌘ Shift + Z` or `⌘ + / ⌘ −`. Useful for plain-text contexts such
 * as `title` tooltips and search matching.
 */
export function formatComboString(combo: string, isMac: boolean): string {
  return combo.replace(/[^\s+/]+/g, token => formatKeyToken(token, isMac))
}
